/**
 * AsyncMediaContext
 * 
 * This context is responsible for subscribing to all attachment questions with async analyses
 * And it will subscribe for updates on those analyses via websockets if the analysis target field is not setInfo
 * 
 * It maintains a state of all jobs, and keeps that state in sync with the associated target fields, which represent the ultimate persisted source of truth
 * 
 * It also provides a `removeImage` method which is used by Attachment to remove a given image url
 * from any of the associated analyses and analysis job target field JSON objects
 */
import React, { PropsWithChildren, useContext, useMemo, useEffect, useReducer, useCallback, useState } from "react";
import * as v0 from "@aidkitorg/types/lib/survey";
import { ApplicantContext } from "./ApplicantContext";
import type { Sections } from "../Apply";
import { useChannel } from "../Realtime";
import { safeParse } from "../Util";
import { flattenAnalyses } from "@aidkitorg/types/lib/util";
import { ChannelName, RealmName, SubscribeEvent, AsyncMediaUpdateEvent, DidSubscribeEvent, QueuedJobOutput } from "@aidkitorg/types/lib/convoai/documentAssistance.types";
import { InfoDict } from "../Questions/Props";
import { useSurveySections } from "./SurveySectionsContext";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { useRoboScreener } from "./RoboScreenerContext";
import { AttachmentJob } from "../Questions/Attachment";

export type AsyncMediaContextType = {
  state: {
    [jobId: string]: AsyncMediaStateItem
  },
  removeImage: (imageUrl:string) => InfoDict,
  analysisTargetFieldsGrouped: {
    targetField: string,
    jobTargetField?: string,
    analysisResultIdTargetField: string
  }[],  
}

export type AsyncMediaStateItem = {
  field: string,
  imageUrl?: string,
  status?: 'QUEUED' | 'PROCESSING' | 'FINISHED' | 'ERRORED',
  value?: string,
  jobId?: string,
  conversationId?: string,
  subscribed?: boolean,
  error?: string
}

export const AsyncMediaContext = React.createContext<AsyncMediaContextType>({
  state: {},
  removeImage: () => {
    console.warn("AsyncMediaContext has not been implemented, ignoring removeImage");
    return {};
  },
  analysisTargetFieldsGrouped: []
});

/* DISPATCH EVENTS */
type UpdateJobDispatchEvent = {
  type: "upsert"
  jobId: string,
  update: Partial<AsyncMediaStateItem> 
}

type RemoveJobDispatchEvent = {
  type: "remove"
  jobId: string
}

type UnsubscribeAllDispatchEvent = {
  type: "unsubscribeAll"
}

type AsyncMediaDispatchEvent = UpdateJobDispatchEvent | UnsubscribeAllDispatchEvent | RemoveJobDispatchEvent;

export const AsyncMediaProvider = (props: PropsWithChildren) => { 
  const { sections } = useSurveySections();
  // passthrough if no sections passed in 
  if (!sections?.sections?.length) { 
    return <>{props.children}</>
  }
  return <AsyncMediaProviderWithSections sections={sections}>
    {props.children}
  </AsyncMediaProviderWithSections>
}

export const AsyncMediaProviderWithSections = (props: { sections: Sections } & PropsWithChildren) => { 
  const { info, update: setInfo, refreshInfo } = useContext(ApplicantContext);
  const { roboscreener } = useRoboScreener();
  const [ready, setReady] = useState<boolean>(false);
  
  // get all attachment questions and get the associated async analysis target fields 
  const analysisTargetFieldsGrouped = useMemo(
    () => { 
      const attachmentQuestions = (props.sections?.sections?.flatMap(section => section.Questions) ?? [])
        .filter(q => q?.["Field Type"] === "Attachment")
        .map(q => ({ ...q, config: safeParse(q.Metadata!) as v0.Attachment | undefined}))
      
      return attachmentQuestions.flatMap(q => { 
        const analyses = flattenAnalyses(q.config?.assistance?.analyses ?? []);
        return analyses
          .map(a => ({
            targetField: a.targetField,
            analysisResultIdTargetField: `${a.targetField}_analysis_result_id`,
            jobTargetField: a.processAsync ? `${a.targetField}_jobs` : undefined
          }));
      })
    },
    [props.sections?.sections?.length]
  );
  const analysisTargetFields = useMemo(() => analysisTargetFieldsGrouped.flatMap(({ targetField, jobTargetField }) => [
    targetField, 
    ...(jobTargetField ? [jobTargetField] : [])
  ]), [analysisTargetFieldsGrouped]);
  
  const sendMessageToChannel = useChannel<SubscribeEvent | DidSubscribeEvent | AsyncMediaUpdateEvent>({
    realm: RealmName,
    name: ChannelName,
    // disable the channel if there are no async attachment assistance
    disabled: !analysisTargetFieldsGrouped.filter(({ jobTargetField }) => !!jobTargetField).length,
    onOpen() {       
      setReady(true);
    },
    onMessage(msg) { 
      switch(msg.event) {
        case "didSubscribe":
          const { jobId: didSubscribeId } = msg.data;
          asyncMediaDispatch({
            type: "upsert",
            jobId: didSubscribeId,
            update: {
              status: "QUEUED"
            }
          });
          break;
        case "asyncMediaUpdate":
          const { jobId, result } = msg.data;
          const status: AsyncMediaStateItem["status"] = result.value ? "FINISHED"
            : (result.error ? "ERRORED" : "PROCESSING");
          // dispatch an update to context
          asyncMediaDispatch({
            type: "upsert",
            jobId,
            update: {
              status,
              ...result
            }
          });
          break;
      }
    },
    onClose() {
      asyncMediaDispatch({
        type: "unsubscribeAll"
      });
      setReady(false);
    }
  }); 
  
  const [state, asyncMediaDispatch] = useReducer(function reducer(state: AsyncMediaContextType["state"], event: AsyncMediaDispatchEvent) : AsyncMediaContextType["state"] {
    switch (event.type) {
      case "upsert":
        return {
          ...state,
          [event.jobId]: {
            ...(state[event.jobId] ?? {}),
            ...event.update
          }
        };
      case "unsubscribeAll":
        return {
          ...state,
          ...Object.entries(state).reduce(
            (acc, [k, v]) => {
              acc[k] = {
                ...v,
                status: "PROCESSING",
                subscribed: false
              }
              return acc;
            },
            {} as AsyncMediaContextType["state"]
          )
        };
      case "remove":
        const original = state[event.jobId];
        if (original) {
          delete state[event.jobId];
        }
        return { ...state };
      default:
        console.warn("Received unknown dispatch event", (event as AsyncMediaDispatchEvent).type);
        return state;
    }
  }, {});
  
  // this useEffect should be invoked anytime a relevant target field value changes
  // it is responsible for repopulating the state and dispatching websocket events
  useAsyncEffect(
    async () => {
      if (!ready) return;
      for (const { targetField, jobTargetField } of analysisTargetFieldsGrouped) {
        const targetFieldValuesByUrl: { [url: string]: string } = safeParse(info[targetField]!, {});
        // job exists, but there is no value, subscribe for updates
        if (jobTargetField) {
          const jobsByImageUrl: { [url: string]: QueuedJobOutput } = safeParse(info[jobTargetField]!, {});          
          for ( const [imageUrl, { jobId }] of Object.entries(jobsByImageUrl)) {
            if (jobId && !targetFieldValuesByUrl[imageUrl] && !state[jobId]?.subscribed) {
              const { status } = sendMessageToChannel({
                realm: RealmName,
                channel: ChannelName,
                event: "subscribe",
                data: {
                  jobId
                }
              });
              if (status === "success") {
                asyncMediaDispatch({
                  type: "upsert",
                  jobId,
                  update: {
                    field: targetField,
                    jobId,
                    subscribed: true,
                    imageUrl: decodeURI(imageUrl)
                  }
                });              
              }
              
              // fixes weird race condition only in dev
              if (process.env.NODE_ENV === "development") { 
                await (new Promise(resolve => setTimeout(resolve, 100)));                
              }
            }
          }          
        }        
      }
    },
    [
      ready,
      analysisTargetFieldsGrouped,
      asyncMediaDispatch,
      sendMessageToChannel,
      ...analysisTargetFields.map(field => info[field])
    ]
  );
  
  // this useEffect is responsible for calling setInfo to update target fields as the state changes
  useEffect(
    () => {      
      // merge updates with existing info
      const currentStateByField = Object.values(state).reduce(
        (acc, { field, imageUrl, jobId, conversationId, value, error }) => {
          if (imageUrl) { 
            imageUrl = decodeURI(imageUrl);
            
            // populate main analysis target field
            const existingValue: Record<string, string> = safeParse(info[field]!, {});
            acc[field] = {
              ...existingValue,
              [imageUrl]: value ?? existingValue[imageUrl] ?? ""
            };

            // populate the error field
            const errorField = `${field}_error`;
            const existingErrorValue: Record<string, string> = safeParse(info[errorField]!, {});
            acc[errorField] = {
              ...existingErrorValue,
              [imageUrl]: error ?? ""
            };

            // populate the analysis result ID field
            const analysisResultIdField = `${field}_analysis_result_id`;
            const existingAnalysisResultIdValue: Record<string, string> = safeParse(info[analysisResultIdField]!, {});
            acc[analysisResultIdField] = {
              ...existingAnalysisResultIdValue,
              [imageUrl]: conversationId ?? existingAnalysisResultIdValue[imageUrl] ?? ""
            };

            // populate job target field
            const { jobTargetField } = analysisTargetFieldsGrouped.find(({ targetField }) => targetField === field) ?? {};
            if (jobTargetField) { 
              const existingJobValue: Record<string, string> = safeParse(info[jobTargetField]!, {});
              acc[jobTargetField] = {
                ...existingJobValue,
                ...(jobId ? {
                  [imageUrl]: {
                    jobId,
                    field
                  }
                } : {})
              };
            }
                        
          }
          return acc;
        },
        {} as { [targetField:string]: { [imageUrl: string]: string | AttachmentJob }}
      );
      
      // populate a list of updates for every analysis target field
      // values not present will get set to ""
      const updates: InfoDict = {};
      for (const [field, value] of Object.entries(currentStateByField)) {
        const updatedValue = JSON.stringify(value);
        if (info[field] !== updatedValue) {
          updates[field] = updatedValue;
        }
      }

      // persist updates using roboscreener.setKey to trigger formula recomputation
      if (Object.keys(updates).length > 0) {
        if (roboscreener) {
          // Use setKey for each field to trigger formula recomputation and dependency tracking
          for (const [field, value] of Object.entries(updates)) {
            if (value !== undefined) {
              roboscreener.setKey(field, value);
            }
          }
          // Also update ApplicantContext with the merged updates to ensure sync
          // Note: roboscreener.info may have additional computed fields updated by computeUpdates,
          // but those will be synced by the useEffect in ModularQuestionPage that watches info
          setInfo({
            ...roboscreener.info,
            ...updates
          });
        } else {
          // Fallback to setInfo if roboscreener is not available
          setInfo({
            ...info,
            ...updates
          });
        }
      }
        
    },
    [analysisTargetFields, setInfo, state, info, roboscreener]
  );
  
  const removeImage = useCallback((imageUrl: string) => { 
    // remove image from state
    for (const [jobId, item] of Object.entries(state)) {
      if (item.imageUrl === imageUrl) { 
        asyncMediaDispatch({
          type: "remove",
          jobId
        });
      }
    }
    // compute a set of target field updates and return to the caller
    const updates: InfoDict = {};
    for (const field of analysisTargetFields) {
      const existingUrlMap: { [url: string]: any } = safeParse(info[field]!, {});
      if (existingUrlMap[imageUrl]) { 
        delete existingUrlMap[imageUrl];
        updates[field] = Object.values(existingUrlMap).length ? JSON.stringify(existingUrlMap) : "";
      }      
    }
    return updates;
  }, [asyncMediaDispatch, state, analysisTargetFields, info, setInfo]);
  
  const context: AsyncMediaContextType = {
    state,
    removeImage,
    analysisTargetFieldsGrouped
  }

  return <AsyncMediaContext.Provider value={context}>
    {props.children}
  </AsyncMediaContext.Provider>;  
}
